import React, { useState,useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, ShieldCheck, ClipboardList, UserCheck, FileText } from 'lucide-react';

export default function AcademicPolicy({ 
  accent = "#115e32",
  pageTitle = "Academic Policy | University of Creative Technology Chittagong",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  const [open, setOpen] = useState(null);

  const toggle = (key) => setOpen((s) => (s === key ? null : key));

  const sections = [
    {
      id: 'integrity',
      title: 'Academic Integrity',
      icon: ShieldCheck,
      body: `Students at UCTC are expected to uphold the highest standards of academic honesty and ethics. Acts of academic misconduct, including plagiarism, cheating, or falsifying data, are strictly prohibited. Violations of academic integrity will result in penalties, which may include grade deductions, suspension, or expulsion. Collaboration is allowed only when explicitly stated in the course guidelines.`
    },
    {
      id: 'attendance',
      title: 'Attendance Policy',
      icon: UserCheck,
      body: `Class attendance is mandatory, and students are required to attend at least 75% of scheduled classes to be eligible for end-of-semester examinations. Absences due to illness, emergencies, or other valid reasons must be documented and communicated to the concerned authorities. Attendance is monitored closely, and repeated non-compliance will affect academic progression.`
    },
    {
      id: 'grading',
      title: 'Grading and Assessment',
      icon: ClipboardList,
      body: `UCTC follows a comprehensive grading system that evaluates students through continuous assessments, midterm and final examinations, and class participation. The minimum GPA for good academic standing is 2.5 on a 4.0 scale. Students are encouraged to review the grading breakdown provided in their course syllabi at the beginning of each semester.`
    },
    {
      id: 'registration',
      title: 'Course Registration and Enrollment',
      icon: BookOpen,
      body: `Students must register for their courses at the beginning of each semester during the designated registration period. Late registrations are subject to additional fees and require approval from the registrar. A specific add/drop period allows students to adjust their course load without penalty. Failing to register in time may result in academic and financial delays.`
    },
    {
      id: 'exams',
      title: 'Examination Regulations',
      icon: FileText,
      body: `Examinations are conducted under strict supervision to ensure fairness and integrity. Students must carry valid university ID cards and admit cards to enter exam halls. Possession of unauthorized materials, disruptive behavior, or impersonation will lead to disqualification and possible disciplinary action. Exam schedules are released well in advance to facilitate preparation.`
    },
    {
      id: 'conduct',
      title: 'Code of Conduct',
      icon: UserCheck,
      body: `All students are expected to behave respectfully towards peers, faculty, and staff. Discriminatory actions, harassment, and disruptive conduct are not tolerated and will be addressed through appropriate disciplinary procedures. The university strives to foster a safe and inclusive learning environment for everyone.`
    },
    {
      id: 'support',
      title: 'Academic Support Services',
      icon: ClipboardList,
      body: `Students who require assistance with any course or department-related matters are encouraged to contact the respective course coordinator or course teacher directly. For academic support, such as clarification of course content or guidance on assignments, the course teacher is the primary point of contact. Students are encouraged to reach out promptly to ensure they receive the necessary help and stay on track with their academic progress.`
    },
    {
      id: 'graduation',
      title: 'Graduation Requirements',
      icon: BookOpen,
      body: `Students must fulfill all academic requirements, including the completion of required credit hours, maintaining a minimum cumulative GPA of 2.5, and completing any required internships, projects, or theses. All financial dues must be cleared, and necessary documents submitted, to qualify for graduation.`
    },
    {
      id: 'complaints',
      title: 'Complaints and Appeals',
      icon: FileText,
      body: `Students who wish to file a formal complaint regarding academic matters must submit their grievance in writing to the Registrar Office. The complaint should include all relevant details and supporting documents. Appeals related to academic decisions or disciplinary actions must also be submitted within seven working days of the decision. The Registrar Office will review the complaint or appeal and provide a resolution. The decision made by the Registrar Office will be final.`
    },
    {
      id: 'leave',
      title: 'Leave of Absence',
      icon: UserCheck,
      body: `Students may apply for a temporary leave of absence for personal, medical, or other valid reasons. Applications must be submitted in writing, along with supporting documents, to the registrar's office. Approved leaves allow students to retain enrollment status, but re-enrollment procedures must be followed upon their return.`
    },
    {
      id: 'semester-drop',
      title: 'Semester Drop Policy',
      icon: ShieldCheck,
      body: `A semester drop allows students to take a break from their studies while retaining their enrollment status. Applications for a semester drop must be submitted with valid reasons, such as health issues or financial challenges, and approved by the academic advisor and department head. Students are allowed to drop up to 3 semesters during their entire 4-year study period. However, students cannot drop more than 2 consecutive semesters. If a student needs to drop more than 3 semesters, he/she must pay a readmission fee. The total study period, including dropped semesters, must not exceed 6 years.`
    },
    {
      id: 'amendments',
      title: 'Amendments to Policies',
      icon: FileText,
      body: `UCTC reserves the right to revise its academic policies to meet evolving academic and administrative needs. Any amendments will be officially communicated to students and faculty through notices and updates on the university website.`
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#6BCCF2] to-teal-50 text-[#014864]">
      {/* Hero with SVG */}
      <header className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 py-20 relative z-20">
          <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-center">
            <div className="inline-flex items-center justify-center bg-amber-100 rounded-full p-4 shadow-md mb-6">
              <BookOpen className="w-12 h-12 text-[#014864]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-[#014864]">Academic Policy</h1>
            <p className="mt-3 max-w-3xl mx-auto text-[#014864]">This comprehensive academic policy framework provides clarity and structure for all students and staff at University of Creative Technology Chittagong.</p>
          </motion.div>
        </div>

        {/* Decorative SVG blobs / waves */}
        <svg className="absolute right-0 top-0 w-1/2 opacity-20" viewBox="0 0 600 600" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(300,300)">
            <path d="M120,-140C160,-100,190,-60,200,-10C210,40,200,100,160,130C120,160,60,160,10,150C-40,140,-80,120,-120,90C-160,60,-200,20,-200,-30C-200,-80,-160,-140,-120,-180C-80,-220,-40,-240,10,-250C60,-260,120,-220,120,-140Z" fill="#fef3c7"/>
          </g>
        </svg>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <aside className="md:col-span-1 bg-white p-6 rounded-2xl shadow border border-amber-100">
            <h3 className="text-xl font-bold text-[#014864]">Quick Links</h3>
            <ul className="mt-4 text-sm text-[#014864] space-y-3">
              <li><a className="hover:underline" href="#integrity">Academic Integrity</a></li>
              <li><a className="hover:underline" href="#attendance">Attendance Policy</a></li>
              <li><a className="hover:underline" href="#grading">Grading and Assessment</a></li>
              <li><a className="hover:underline" href="#semester-drop">Semester Drop Policy (PDF)</a></li>
            </ul>
          </aside>

          <section className="md:col-span-2 space-y-6">
            {/* Accordion Sections */}
            {sections.map((s) => {
              const Icon = s.icon;
              return (
                <article key={s.id} id={s.id} className="bg-white rounded-2xl shadow p-6 border border-amber-100">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-amber-50 p-3">
                      <Icon className="w-6 h-6 text-[#014864]" />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-bold text-[#014864]">{s.title}</h4>
                        <button onClick={() => toggle(s.id)} className="text-amber-600 font-semibold">{open === s.id ? 'Hide' : 'Read'}</button>
                      </div>

                      <AnimatePresence initial={false}>
                        {open === s.id ? (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.35 }} className="mt-3 text-[#014864] text-sm">
                            <p>{s.body}</p>
                          </motion.div>
                        ) : (
                          <motion.div initial={{ opacity: 1 }} animate={{ opacity: 1 }} className="mt-3 text-[#014864] text-sm line-clamp-3">
                            <p>{s.body}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </article>
              );
            })}

            {/* Additional Policies */}
            <article className="bg-white rounded-2xl shadow p-6 border border-amber-100">
              <h4 className="text-lg font-bold text-[#014864]">Complaints, Leave & Appeals</h4>
              <p className="mt-3 text-[#014864] text-sm">Students who wish to file a formal complaint regarding academic matters must submit their grievance in writing to the Registrar Office. Appeals related to academic decisions or disciplinary actions must also be submitted within seven working days of the decision.</p>
            </article>

            <article className="bg-white rounded-2xl shadow p-6 border border-amber-100">
              <h4 className="text-lg font-bold text-[#014864]">Semester Drop Policy — PDF</h4>
              <p className="mt-3 text-[#014864] text-sm">Students are allowed to drop up to 3 semesters during their entire 4-year study period. Please refer to the full policy for details.</p>
              <div className="mt-4">
                <a href={`${import.meta.env.VITE_API_SOURCE_URL}/policies/semester-drop.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#014864] text-white font-semibold shadow">Download PDF</a>
              </div>
            </article>

            <article className="bg-amber-50 rounded-2xl p-6 border border-amber-100 text-[#014864]">
              <h4 className="text-lg font-bold">Need help?</h4>
              <p className="mt-2 text-sm">For academic support, contact your course coordinator or the Registrar's Office. All policy amendments will be communicated through official notices.</p>
            </article>
          </section>
        </div>
      </main>
    </div>
  );
}
